// hooks/reviews/useReviews.ts
import { useState, useEffect, useCallback } from "react";
import { Review, ReviewToBackend } from "../../../types/features/Review";
import { reviewService } from "../../../services/reviewService";
import useToken from "../auth/useToken";

export default function useReviews() {
  const { token } = useToken();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchReviews = useCallback(async (page: number = 1, perPage: number = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response = await reviewService.getAll(page, perPage);
      setReviews(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des avis");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchReviewById = useCallback(async (id: string): Promise<Review | null> => {
    setLoading(true);
    setError(null);
    try {
      const review = await reviewService.getById(id);
      if (!review) {
        setError("Avis non trouvé");
      }
      return review;
    } catch (err) {
      setError("Erreur lors du chargement de l'avis");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔒 PROTÉGÉ - Token requis
  const createReview = useCallback(async (data: ReviewToBackend): Promise<Review | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const newReview = await reviewService.create(token , data);
      setReviews((prev) => [newReview, ...prev]);
      return newReview;
    } catch (err) {
      setError("Erreur lors de la création de l'avis");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const updateReview = useCallback(async (id: string, data: Partial<ReviewToBackend>): Promise<Review | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const updatedReview = await reviewService.update(token, id, data);
      setReviews((prev) => prev.map((r) => (r.id === id ? updatedReview : r)));
      return updatedReview;
    } catch (err) {
      setError("Erreur lors de la mise à jour de l'avis");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteReview = useCallback(async (id: string): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await reviewService.delete(token, id);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression de l'avis");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteManyReviews = useCallback(async (ids: string[]): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await reviewService.deleteMany(token, ids);
      setReviews((prev) => prev.filter((r) => !ids.includes(r.id)));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression multiple");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const toggleVisibility = useCallback(async (id: string, currentStatus: boolean): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    try {
      const updated = await reviewService.update(token, id, { is_visible: !currentStatus });
      setReviews((prev) => prev.map((r) => (r.id === id ? updated : r)));
      return true;
    } catch (err) {
      setError("Erreur lors du changement de visibilité");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  return {
    reviews,
    loading,
    error,
    pagination,
    fetchReviews,
    fetchReviewById,
    createReview,
    updateReview,
    deleteReview,
    deleteManyReviews,
    toggleVisibility,
  };
}